/**
 * React Query hooks for the administration of the digital resource connectors.
 * The portal side lives in features/portal (it uses the patron's session).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/api/client";
import type {
  DigitalResourceCheckResult,
  DigitalResourceSettings,
  DigitalResourceSettingsInput,
  DigitalResourcesOverview,
} from "@/api/types";

const OVERVIEW_KEY = ["digital-resources", "settings"];

export function useDigitalResourcesSettings(options: { pollWhileHarvesting?: boolean } = {}) {
  return useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api.get<DigitalResourcesOverview>("/admin/digital-resources"),
    // A queued or running harvest finishes in the background worker
    refetchInterval: (query) =>
      options.pollWhileHarvesting &&
      query.state.data?.resources.some(
        (r) => r.harvest.status === "queued" || r.harvest.status === "running"
      )
        ? 5000
        : false,
  });
}

function useReplaceResource() {
  const queryClient = useQueryClient();

  return (resource: DigitalResourceSettings) => {
    queryClient.setQueryData<DigitalResourcesOverview>(OVERVIEW_KEY, (overview) =>
      overview
        ? {
            ...overview,
            resources: overview.resources.map((r) => (r.id === resource.id ? resource : r)),
          }
        : overview
    );
  };
}

export function useSaveDigitalResource(id: string) {
  const replace = useReplaceResource();

  return useMutation({
    mutationFn: (input: DigitalResourceSettingsInput) =>
      api.put<DigitalResourceSettings>(`/admin/digital-resources/${id}`, input),
    onSuccess: replace,
  });
}

export function usePreviewDigitalResourceLink(id: string) {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>(`/admin/digital-resources/${id}/preview`),
  });
}

export function useCheckDigitalResourceRepository(id: string) {
  return useMutation({
    mutationFn: () =>
      api.post<DigitalResourceCheckResult>(`/admin/digital-resources/${id}/oai-check`),
  });
}

export function useHarvestDigitalResource(id: string) {
  const replace = useReplaceResource();

  return useMutation({
    mutationFn: (full: boolean) =>
      api.post<DigitalResourceSettings>(`/admin/digital-resources/${id}/harvest`, { full }),
    onSuccess: replace,
  });
}
